import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Download, X } from 'lucide-react';
import { useModalAccessibility } from '../hooks/useModalAccessibility';

interface ChatImageGalleryProps {
  images: string[];
  isOptimistic?: boolean;
  onDownload?: (index: number) => Promise<void>;
}

export const getSwipeImageIndex = (index: number, count: number, dx: number, dy: number) => (
  Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.25
    ? Math.max(0, Math.min(count - 1, index + (dx < 0 ? 1 : -1)))
    : index
);

const iconButtonClass = 'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-panel/10 text-white transition hover:bg-panel/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line';
const navigationButtonClass = 'absolute top-1/2 z-20 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-line/40 bg-canvas/80 text-ink shadow-lg hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris';

const getDownloadName = (src: string, index: number) => {
  try {
    const pathname = decodeURIComponent(new URL(src, window.location.href).pathname);
    const fileName = pathname.split('/').pop();
    return fileName && /\.[a-z\d]{2,5}$/i.test(fileName) ? fileName : `imagen-${index + 1}`;
  } catch {
    return `imagen-${index + 1}`;
  }
};

const ChatImageGallery: React.FC<ChatImageGalleryProps> = ({ images, isOptimistic = false, onDownload }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const currentIndex = activeIndex ?? 0;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const imageList = images.filter(Boolean);
  const hasLightbox = activeIndex !== null && imageList[activeIndex] !== undefined;

  useModalAccessibility(dialogRef, {
    enabled: hasLightbox,
    onClose: () => setActiveIndex(null),
    initialFocusRef: closeButtonRef
  });

  if (imageList.length === 0) return null;

  const activeImage = hasLightbox ? imageList[currentIndex] : null;
  const downloadCurrent = async () => {
    if (!activeImage || !onDownload || activeImage.startsWith('blob:')) return;
    setDownloadError(false);
    try {
      await onDownload(currentIndex);
    } catch {
      setDownloadError(true);
    }
  };
  const lightbox = hasLightbox && activeImage && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-canvas/95 p-3 sm:p-6"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setActiveIndex(null);
        }}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Imagen ${currentIndex + 1} de ${imageList.length}`}
          tabIndex={-1}
          className="relative flex h-full max-h-[92dvh] w-full max-w-6xl flex-col items-center justify-center outline-none"
        >
          <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 pt-[env(safe-area-inset-top,0px)]">
            <span className="rounded-full bg-black/40 px-3 py-2 text-sm font-medium text-white" aria-live="polite">
              {currentIndex + 1} / {imageList.length}
            </span>
            <div className="flex items-center gap-2">
              {onDownload && !activeImage.startsWith('blob:') ? <button
                type="button"
                onClick={downloadCurrent}
                className={iconButtonClass}
                aria-label={`Descargar imagen ${currentIndex + 1} original`}
              >
                <Download size={18} aria-hidden="true" />
              </button> : <a href={activeImage} download={getDownloadName(activeImage, currentIndex)} className={iconButtonClass} aria-label={`Descargar imagen ${currentIndex + 1} original`}><Download size={18} aria-hidden="true" /></a>}
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setActiveIndex(null)}
                className={iconButtonClass}
                aria-label="Cerrar visualización de imágenes"
              >
                <X size={22} aria-hidden="true" />
              </button>
            </div>
          </div>

          {downloadError && <p role="alert" className="absolute top-16 rounded-lg bg-danger-action px-3 py-2 text-sm text-white">No se pudo descargar la imagen.</p>}

          <div
            className="relative flex min-h-0 max-w-full items-center justify-center touch-pan-y"
            onTouchStart={(event) => {
              const touch = event.touches[0];
              touchStartRef.current = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
            }}
            onTouchCancel={() => { touchStartRef.current = null; }}
            onTouchEnd={(event) => {
              const start = touchStartRef.current;
              touchStartRef.current = null;
              const touch = event.changedTouches[0];
              if (!start || !touch || event.touches.length) return;
              setActiveIndex(getSwipeImageIndex(currentIndex, imageList.length, touch.clientX - start.x, touch.clientY - start.y));
            }}
          >
          <img
            src={activeImage}
            alt={`Imagen adjunta ${currentIndex + 1}`}
            className="max-h-[calc(92dvh-7rem)] max-w-full select-none object-contain"
            draggable={false}
          />
          </div>

          {currentIndex > 0 && (
              <button
                type="button"
                onClick={() => setActiveIndex(currentIndex - 1)}
                className={`${navigationButtonClass} left-0 sm:left-2`}
                aria-label="Ver imagen anterior"
              >
                <ArrowLeft size={22} aria-hidden="true" />
              </button>
          )}
          {currentIndex < imageList.length - 1 && (
              <button
                type="button"
                onClick={() => setActiveIndex(currentIndex + 1)}
                className={`${navigationButtonClass} right-0 sm:right-2`}
                aria-label="Ver imagen siguiente"
              >
                <ArrowRight size={22} aria-hidden="true" />
              </button>
          )}
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <>
      <div className="mb-2 flex w-fit max-w-full flex-nowrap gap-1.5 overflow-hidden rounded-xl">
        {imageList.map((src, index) => (
          <button
            key={`${src}-${index}`}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="group relative min-w-0 shrink overflow-hidden rounded-lg bg-black/10 ring-1 ring-black/5 transition hover:ring-2 hover:ring-juris focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris"
            aria-label={`Ampliar imagen ${index + 1} de ${imageList.length}`}
          >
            <img
              src={src}
              alt=""
              loading={isOptimistic ? 'eager' : 'lazy'}
              decoding={isOptimistic ? 'sync' : 'async'}
              className="block h-[4.5rem] w-auto max-w-full object-contain sm:h-24"
            />
          </button>
        ))}
      </div>
      {lightbox}
    </>
  );
};

export default ChatImageGallery;
